import { apolloClient } from './apollo/client';
import { GET_PRODUCTS, GET_PURCHASABLE_PRODUCTS } from './apollo/queries';
import { Product, Coupon } from '../../types';
import { MembershipApiProduct, MembershipApiCoupon } from '../../types/membershipApi';

interface Options {
    isPurchasableOnly?: boolean;
}

export const getProducts = async ({ isPurchasableOnly = true }: Options = {}): Promise<Product[]> => {
    try {
        let productsData: MembershipApiProduct[] | undefined;

        if (isPurchasableOnly) {
            const { data, error } = await apolloClient.query<{ getPurchasableProducts: MembershipApiProduct[] }>({
                query: GET_PURCHASABLE_PRODUCTS,
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
            });
            if (error && !data) {
                throw new Error(`GraphQL errors: ${error.message}`);
            }
            productsData = data?.getPurchasableProducts;
        } else {
            const { data, error } = await apolloClient.query<{ getProducts: MembershipApiProduct[] }>({
                query: GET_PRODUCTS,
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
            });
            if (error && !data) {
                throw new Error(`GraphQL errors: ${error.message}`);
            }
            productsData = data?.getProducts;
        }

        // Helper function to map coupons from API to frontend types
        const mapCoupons = (coupons: MembershipApiCoupon[]): Coupon[] => {
            return coupons.map(coupon => ({
                id: coupon.id,
                priceId: coupon.priceId,
                stripeCouponId: coupon.stripeCouponId,
                // Our business logic fields
                isActive: coupon.isActive,
                pillDescription: coupon.pillDescription ?? null, // Keep null if not provided, handle undefined
                // Stripe fields
                stripeName: coupon.name,
                percent_off: coupon.percentOff,
                amount_off: coupon.amountOff,
                currency: coupon.currency,
                duration: coupon.duration,
                duration_in_months: coupon.durationInMonths,
                redeem_by: coupon.redeemBy,
                // Internal fields
                createdAt: coupon.createdAt,
                updatedAt: coupon.updatedAt
            }));
        };
        if (!productsData || !Array.isArray(productsData)) {
            throw new Error('Failed to fetch products: Invalid response from server');
        }

        // Map membership-api product format to frontend product format
        return productsData.map((product: MembershipApiProduct): Product => ({
            id: product.id,  // Use Data Connect UUID as primary ID
            title: product.name,
            name: product.name,
            description: product.description || '',
            tierId: product.tierId,
            stripeProductId: product.stripeProductId,
            createdAt: product.createdAt,
            pricing: {
                isPurchasableQuery: isPurchasableOnly,
                monthly: product.pricing.monthly ? {
                    id: product.pricing.monthly.id,
                    amountCents: product.pricing.monthly.amountCents,
                    currency: product.pricing.monthly.currency,
                    coupons: mapCoupons(product.pricing.monthly.coupons || []),
                    isPurchasable: product.pricing.monthly.isPurchasable,
                    trialPeriodDays: product.pricing.monthly.trialPeriodDays ?? null,
                } : undefined,
                annual: product.pricing.annual ? {
                    id: product.pricing.annual.id,
                    amountCents: product.pricing.annual.amountCents,
                    currency: product.pricing.annual.currency,
                    coupons: mapCoupons(product.pricing.annual.coupons || []),
                    isPurchasable: product.pricing.annual.isPurchasable,
                    trialPeriodDays: product.pricing.annual.trialPeriodDays ?? null,
                } : undefined
            },
            price: product.pricing.monthly?.amountCents || 0, // Use monthly price in cents  
            rewards: [], // Product rewards from backend
            monthlyPrice: product.pricing.monthly?.amountCents || 0,
            annualPrice: product.pricing.annual?.amountCents || 0,
            currency: product.pricing.monthly?.currency || 'usd',
        }));
    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
};